// @flow
import { get, isEmpty, pick, flatten } from 'lodash';
import moment from 'moment';
import * as yup from 'yup';
import { type Formik } from 'formik';

import { getAdultsCivilities } from '@clubmed/domains-service';
import { windowWrapper } from '@clubmed/utils';

import graphQLClient from 'Utils/graphql-client/graphqlClient';
import redirectUtils from 'Utils/uri/redirectUtils';
import { buildUrl } from 'Routes/routeBuilder';
import {
  STATUS_CODE_200,
  STATUS_CODE_400,
  STATUS_CODE_401,
  STATUS_CODE_500,
} from 'Utils/status-code/statusCode.constants';
import DynamicLinkUtil from 'Components/frameworks/dynamic-link/DynamicLinkUtil';
import { PROPOSAL_UPDATE } from 'Utils/request/requestDictionary';
import type {
  AdultAttendeeWithInfosType,
  AttendeesWithInfosType,
  ChildAttendeeWithInfosType,
} from 'Store/booking/booking.selector';
import type { State as userInfoType } from 'Store/user-info/reducers/userInfoReducer';
import {
  EMAIL_OPTIN,
  FOSUN_PARTNERS,
  MAIL_OPTIN,
  SMS_OPTIN,
  PHONE_OPTIN,
  CLUBMED_PLUS,
} from 'Utils/legal-information/legalInformationIds.constants';
import { apiDateToTimestamp } from 'Utils/date/apiDate';
import { emailRegex, getPhoneRegex } from 'Utils/regex/RegexHelper';
import { CNIL_LEGAL_MESSAGE_ID, MAX_AGE, YEAR_OF_MAJORITY } from './constants';

export type LegalType = {
  id: string,
  label: string,
  description: string,
  required: boolean,
  concernedAttendees: ?string,
};

function getCNILLegalMessage(
  legalMessages: $ReadOnlyArray<{ id: string, label: string, description: string }> = [],
): ?{ label: string, description: string, required?: boolean } {
  const cnilLegalMessage = legalMessages.find(
    legalMessage => legalMessage.id === CNIL_LEGAL_MESSAGE_ID,
  );
  return cnilLegalMessage && pick(cnilLegalMessage, ['label', 'description', 'required']);
}

function getLegalInputInformation(
  legalMessages: $ReadOnlyArray<LegalInformation> = [],
  id: string,
) {
  return legalMessages.find(legalMessage => legalMessage.id === id);
}

function getLegalInformation(
  beLegalInfos: ?$ReadOnlyArray<LegalInformation>,
): string => ?LegalInformation {
  return id => (beLegalInfos || []).find(legalInfo => legalInfo.id === id);
}

export const getOptinsInfos = (bookingProcess: $PropertyType<CMSContent, 'bookingProcess'>) => {
  const getLegalInfo = getLegalInformation(bookingProcess);

  return {
    email: getLegalInfo(EMAIL_OPTIN),
    sms: getLegalInfo(SMS_OPTIN),
    mail: getLegalInfo(MAIL_OPTIN),
    fosun: getLegalInfo(FOSUN_PARTNERS),
    phone: getLegalInfo(PHONE_OPTIN),
    clubmedPlus: getLegalInfo(CLUBMED_PLUS),
  };
};

function shouldDisplayLegalInformation(
  beLegalInfos: $ReadOnlyArray<LegalInformation>,
): string => boolean {
  const getLegalInfo = getLegalInformation(beLegalInfos);

  return id => !!getLegalInfo(id);
}

function redirectToUri(): string => () => void {
  return (uri: string): (() => void) => {
    return (): void => {
      const redirectUri = new DynamicLinkUtil(windowWrapper.getLocation(), uri).toString();
      redirectUtils.redirectTo()(redirectUri);
    };
  };
}

function _getOptionSubmissionRequest(appContext, params) {
  return `
    mutation {
      bookings(
        proposalId: "${params.proposalId}"
        bookingStatus: OPTION
        ${params.sellerEmail ? `sellerEmail: "${params.sellerEmail}"` : ''}
      ) {
        bookingId
      }
    }`;
}

function submitOption(
  appContext: AppContext,
  location: BookingRouterLocation,
  sellerEmail?: string,
): (*) => Promise<*> {
  const { selectedProposalId, product: productId } = location.queryParams;

  const redirectTo = redirectToUri();
  return (proposal: Proposal) => {
    const params = {
      proposalId: get(proposal, 'id') || '',
      sellerEmail,
    };

    const query = _getOptionSubmissionRequest(appContext, params);

    return graphQLClient
      .runNoCacheQuery(query, appContext, { requestName: PROPOSAL_UPDATE })
      .then(response => {
        redirectTo(
          buildUrl(
            'optionConfirmation',
            { status: STATUS_CODE_200 },
            {
              bi: get(response, 'bookings.bookingId', ''),
              selectedProposalId,
              productId,
            },
          ),
        )();
      })
      .catch(error => {
        const status = get(error, 'errors[0].responseBody.status_code', STATUS_CODE_400);
        const error_code = get(
          error,
          'errors[0].responseBody.errors[0].error_code',
          'BOOKING_UNAVAILABLE',
        );
        const error_description = get(error, 'errors[0].responseBody.errors[0].error_description');

        redirectTo(
          buildUrl(
            'optionConfirmationError',
            {
              status,
              error_code,
              error_description,
            },
            {
              selectedProposalId,
              productId,
            },
          ),
        )();
      });
  };
}

function renderErrorUpdatingAttendees(statusCode: number, i18n?: *) {
  let message = '';
  switch (statusCode) {
    case STATUS_CODE_500:
    case STATUS_CODE_401:
      message = get(i18n, 'booking.form.errors.technicalErrorParticipants');
      break;
    default:
      message = get(i18n, 'booking.form.errorLabel');
      break;
  }
  return message;
}

const getAttendeeId = ({ neolid }) => neolid;

const getAllAttendeesIds = ({
  adults,
  kids,
}: TripInformation): { adultsAttendeesIds?: Array<string>, kidsAttendeesIds?: Array<string> } => {
  const allAttendeesIds = {};

  if (adults.length > 0) {
    allAttendeesIds.adultsAttendeesIds = adults.map(getAttendeeId).filter(Boolean);
  }

  if (kids.length > 0) {
    allAttendeesIds.kidsAttendeesIds = kids.map(getAttendeeId).filter(Boolean);
  }

  return allAttendeesIds;
};

type AdultInitialAttendeesListType = {
  ...AdultAttendeeWithInfosType,
  isMain: boolean,
};

export type InitialAttendeesListType = {
  adults: $ReadOnlyArray<AdultInitialAttendeesListType>,
  children: $ReadOnlyArray<ChildAttendeeWithInfosType>,
  all: $ReadOnlyArray<
    | {
        ...AdultInitialAttendeesListType,
        isMain: boolean,
        attendeeIndex: number,
      }
    | {
        ...ChildAttendeeWithInfosType,
        isChild: boolean,
        attendeeIndex: number,
      },
  >,
};

type InitialAttendeesListArgs = {
  attendees: AttendeesWithInfosType,
  connectedUserInfo: userInfoType,
};
export const getInitialAttendeesList = ({
  attendees,
  connectedUserInfo,
}: InitialAttendeesListArgs): InitialAttendeesListType => {
  // defined the default lastName to that of connectedUser
  const adults: $ReadOnlyArray<AdultInitialAttendeesListType> = attendees.adults.map(
    (adult, index) => ({
      ...adult,
      lastName: connectedUserInfo && !adult.lastName ? connectedUserInfo.lastName : adult.lastName,
      isMain:
        index === 0 ||
        (!!adult.gmNumber &&
          index === attendees.adults.findIndex(({ gmNumber }) => gmNumber === adult.gmNumber)),
    }),
  );
  // defined the default lastName to this of connectedUser
  const children: $ReadOnlyArray<ChildAttendeeWithInfosType> = attendees.children.map(child => ({
    ...child,
    lastName: connectedUserInfo && !child.lastName ? connectedUserInfo.lastName : child.lastName,
  }));

  return {
    adults,
    children,
    all: [
      ...adults.map((adult, index) => ({
        ...adult,
        attendeeIndex: index,
      })),
      ...children.map((child, index) => ({
        ...child,
        attendeeIndex: index,
        isChild: true,
      })),
    ],
  };
};

type GetInitialFormValuesArgsType = {
  i18n: CMSContent,
  initialAttendeesList: InitialAttendeesListType,
  connectedUserInfo: userInfoType,
  hasAddress: boolean,
  nationalityFieldRules?: LegalInformation,
  locale: string,
  sellerEmail: ?string,
};

type GetInitialFormValuesType = {
  adults: $ReadOnlyArray<{
    ...AdultAttendeeWithInfosType,
    birthdate: ?timestamp,
  }>,
  children: $ReadOnlyArray<{
    ...ChildAttendeeWithInfosType,
    birthdate: ?timestamp,
  }>,
};

export const getInitialFormValues = ({
  i18n,
  initialAttendeesList,
  connectedUserInfo,
  hasAddress,
  nationalityFieldRules,
  locale,
  sellerEmail,
}: GetInitialFormValuesArgsType): GetInitialFormValuesType => {
  const showOnlyCountryCode = i18n.booking.participants.postalAddress.displayCountryFieldOnly;
  const optins = getOptinsInfos(i18n.bookingProcess);

  // The opt-ins show are configurable from the CMS, but we also don't show them for identified
  // users who already checked them
  const getOptinValue = (optin, optinName, userInfo) => {
    if (!optin) {
      return;
    }

    // in case of a connected user return undefined to not show it if already checked
    if (connectedUserInfo[optinName] === true) {
      return undefined;
    }

    if (userInfo[optinName] !== undefined) {
      return userInfo[optinName];
    }

    return optin.preChecked;
  };

  return {
    seller: {
      email: sellerEmail,
    },
    // $FlowFixMe temporarily disabled: waiting for clic evolv
    adults: initialAttendeesList.adults.map(adult => ({
      ...adult,
      passport: {
        ...adult.passport,
        ...(nationalityFieldRules &&
        (nationalityFieldRules.concernedAttendees === 'all' || adult.isMain) &&
        (!adult.passport || !adult.passport.nationality)
          ? { nationality: 'RU' }
          : {}),
      },
      birthdate:
        typeof adult.birthdate === 'number' ? adult.birthdate : apiDateToTimestamp(adult.birthdate),
      address:
        adult.isMain && hasAddress
          ? {
              ...(showOnlyCountryCode ? null : adult.address),
              countryCode: get(adult.address, 'countryCode', locale.split('-')[1]),
            }
          : undefined,
      ...(adult.isMain
        ? {
            optinEmail: getOptinValue(optins.email, 'optinEmail', adult),
            optinSms: getOptinValue(optins.sms, 'optinSms', adult),
            optinMail: getOptinValue(optins.mail, 'optinMail', adult),
            optinFosun: getOptinValue(optins.fosun, 'optinFosun', adult),
            optinPhone: getOptinValue(optins.phone, 'optinPhone', adult),
            optinClubmedPlus: getOptinValue(optins.clubmedPlus, 'optinClubmedPlus', adult),
            legalAgreement: false,
            checkAll: false,
          }
        : null),
      // clear user info we don't want to store in the form state and in the store
      isMain: undefined,
      mainContactId:
        !adult.gmNumber && initialAttendeesList.adults[0].neolid
          ? initialAttendeesList.adults[0].neolid
          : '-1', // temporarily disabled: waiting for clic evolv
    })),
    // $FlowFixMe temporarily disabled: waiting for clic evolv
    children: initialAttendeesList.children.map(child => ({
      ...child,
      passport: {
        ...child.passport,
        ...(nationalityFieldRules &&
        nationalityFieldRules.concernedAttendees === 'all' &&
        (!child.passport || !child.passport.nationality)
          ? { nationality: 'RU' }
          : {}),
      },
      birthdate:
        typeof child.birthdate === 'number' ? child.birthdate : apiDateToTimestamp(child.birthdate),
      mainContactId:
        !child.gmNumber && initialAttendeesList.adults[0].neolid
          ? initialAttendeesList.adults[0].neolid
          : '-1', // temporarily disabled: waiting for clic evolv
    })),
  };
};

export function getAddressValidators(
  addressSchema: Array<ValidationSchema>,
  { withRequired = true, message }: { withRequired?: boolean, message?: string } = {},
) {
  return addressSchema.reduce((addressValidators, fieldValidation: ValidationSchema) => {
    let rule = yup.string();
    fieldValidation.validators.forEach(validator => {
      switch (validator.name) {
        case 'pattern':
          rule = rule.matches(validator.value, message);
          break;
        case 'minLength':
          rule = rule
            .transform(value => (value === '' ? undefined : value), message)
            .min(validator.value, message);
          break;
        case 'maxLength':
          rule = rule.max(validator.value, message);
          break;

        default:
          // eslint-disable-next-line no-console
          console.error(`Cannot handle rule ${validator.name}`);
      }
    });

    if (withRequired) {
      if (fieldValidation.required) {
        rule = rule.required();
      }
    }

    return {
      ...addressValidators,
      [fieldValidation.name]: rule,
    };
  }, {});
}

type GetValidatorsArgsType = {
  passportInformation: *,
  expirationDate: *,
  issuingDate: *,
  departureDate: *,
  arrivalDate: *,
  nationality: *,
  identityNumberInformation: *,
  addressSchema: Array<ValidationSchema>,
  locale: string,
  adultCivilities: Array<string>,
  childCivilities: Array<string>,
  userIsSeller: boolean,
};

export type ValidatorsType = {
  adultCivilities: () => boolean,
  childCivilities: () => boolean,
  firstName: () => boolean,
  lastName: () => boolean,
  unicodeFirstName: () => boolean,
  unicodeLastName: () => boolean,
  adultBirthDate: () => boolean,
  childBirthDate: () => boolean,
  email: () => boolean,
  mobile: () => boolean,
  emailNotRequired: () => boolean,
  mobileNotRequired: () => boolean,
  passportID: () => boolean,
  identityNumber: () => boolean,
  legalAgreement: () => boolean,
  gmNumber: () => boolean,
  expirationDate: () => boolean,
  documentNumber: () => boolean,
  issuingDate: () => boolean,
  nationality: () => boolean,
  address: $ReadOnly<{
    number: () => boolean,
    street: () => boolean,
    addOn: () => boolean,
    town: () => boolean,
    city: () => boolean,
    zipCode: () => boolean,
    stateOrDistrict: () => boolean,
    country: () => boolean,
    countryCode: () => boolean,
    additionalInformation1: () => boolean,
    additionalInformation2: () => boolean,
  }>,
};

export const getValidators = ({
  nationality,
  identityNumberInformation,
  addressSchema,
  passportInformation,
  expirationDate,
  issuingDate,
  locale,
  departureDate,
  arrivalDate,
  adultCivilities,
  childCivilities,
  userIsSeller,
}: GetValidatorsArgsType): ValidatorsType => {
  const strictNameValidator = yup.string().matches(/^[^0-9!@#$%^&*()_+=\[\]{};:"\\|,<>\/?]+$/);

  const baseWordValidator = strictNameValidator.matches(/^[\x20-\xFF]+$/); // ASCII
  const phoneRegex = getPhoneRegex(locale);
  const rawValidators = {
    adultCivilities: yup
      .string()
      .oneOf(adultCivilities)
      .required(),
    childCivilities: yup
      .string()
      .oneOf(childCivilities)
      .required(),
    firstName: baseWordValidator.max(20).required(),
    lastName: baseWordValidator.max(30).required(),
    unicodeFirstName: strictNameValidator.max(100).required(),
    unicodeLastName: strictNameValidator.max(100).required(),
    adultBirthDate: yup
      .date()
      .transform((value, initialValue) => {
        return initialValue ? new Date(initialValue) : initialValue;
      })
      .max(moment().subtract(YEAR_OF_MAJORITY, 'y'))
      .min(moment().subtract(MAX_AGE, 'y'))
      .required(),
    childBirthDate: yup
      .date()
      .transform((value, initialValue) => {
        return initialValue ? new Date(initialValue) : initialValue;
      })
      .max(moment())
      .min(moment().subtract(YEAR_OF_MAJORITY, 'y'))
      .required(),
    email: yup
      .string()
      .matches(emailRegex)
      .required(),
    mobile: yup
      .string()
      .matches(phoneRegex)
      .required(),
    emailNotRequired: yup.string().email(),
    mobileNotRequired: yup
      .string()
      .transform(v => (!v ? undefined : v)) // transform to undefined when empty string to pass the regex
      .matches(phoneRegex),
    documentNumber: (() => {
      if (!passportInformation) {
        return;
      }
      const rule = yup
        .string()
        .transform((value, initialValue) => {
          return initialValue ? initialValue.replace(/ /g, '') : initialValue;
        })
        .matches(/[a-zA-Z0-9]{6,30}/, { excludeEmptyString: true });

      return passportInformation.required && !userIsSeller ? rule.required() : rule;
    })(),
    expirationDate: (() => {
      if (!expirationDate) {
        return;
      }
      const rule = yup
        .mixed()
        .transform((value, initialValue) => {
          return initialValue ? new Date(initialValue) : initialValue;
        })
        .test('', '', value => {
          //<< optionnal for seller >> && << cms .required - expirationDate at least one day after departure for user >>
          if (
            (expirationDate.required && !userIsSeller && !value) ||
            ((!expirationDate.required || userIsSeller) &&
              value &&
              value <= moment(departureDate).add(1, 'd')) ||
            (expirationDate.required && !userIsSeller && !value)
          ) {
            return false;
          }
          return true;
        });
      return rule;
    })(),
    issuingDate: (() => {
      if (!issuingDate) {
        return;
      }
      const rule = yup
        .mixed()
        .transform((value, initialValue) => {
          return initialValue ? new Date(initialValue) : initialValue;
        })
        .test('', '', value => {
          //<< optionnal for seller >> && << cms .required - issuingDate at least one day before arrival for user >>
          if (
            (issuingDate.required && !userIsSeller && !value) ||
            ((!issuingDate.required || userIsSeller) &&
              value &&
              value >= moment(arrivalDate).subtract(1, 'd'))
          ) {
            return false;
          }
          return true;
        });
      return rule;
    })(),
    nationality: (() => {
      if (!nationality) {
        return;
      }

      const rule = yup.string();
      return nationality.required && !userIsSeller ? rule.required() : rule;
    })(),

    identityNumber: (() => {
      if (!identityNumberInformation) {
        return;
      }

      const rule = yup
        .string()
        .transform((value, initialValue) => {
          return initialValue ? initialValue.replace(/ /g, '') : initialValue;
        })
        .min(6)
        .max(30);
      return identityNumberInformation.required ? rule.required() : rule;
    })(),
    legalAgreement: yup
      .mixed()
      .oneOf([true])
      .required(),
    gmNumber: yup.string().required(),
  };
  // $FlowFixMe
  return { ...rawValidators, address: getAddressValidators(addressSchema) };
};

const addressSchemaOrder = [
  'additionalInformation1',
  'additionalInformation2',
  'streetNumber',
  'streetName',
  'addOn',
  'zipCode',
  'town',
  'stateOrDistrict',
  'city',
  'countryCode',
];

export const addressSchemaSort = (schema1: ValidationSchema, schema2: ValidationSchema) =>
  addressSchemaOrder.indexOf(schema1.name) - addressSchemaOrder.indexOf(schema2.name);

export const getAdultCivilities = (defaultCivilities: *, locale: string) => {
  const adultCivilities = getAdultsCivilities(locale);

  return isEmpty(adultCivilities) ? defaultCivilities : adultCivilities;
};

type GetHeaderContactInfoTitleArgsType = {
  i18n: CMSContent,
  isValid: boolean,
  isMain: boolean,
  isChild: boolean,
  displayUnicodeFields: boolean,
  formValues: {},
  attendeeIdentifier: string,
  attendeeIndex: number,
};

export const getHeaderContactInfoTitle = ({
  i18n,
  isValid,
  isMain,
  isChild,
  displayUnicodeFields,
  formValues,
  attendeeIdentifier,
  attendeeIndex,
}: GetHeaderContactInfoTitleArgsType) => {
  if (isValid) {
    if (displayUnicodeFields) {
      return `${get(formValues, `${attendeeIdentifier}.unicodeFirstName`)} ${get(
        formValues,
        `${attendeeIdentifier}.unicodeLastName`,
      )}`;
    }

    return `${get(formValues, `${attendeeIdentifier}.firstName`)} ${get(
      formValues,
      `${attendeeIdentifier}.lastName`,
    )}`;
  }

  if (isMain) {
    return i18n.booking.participants.personnalInfosHeader.title;
  }

  if (isChild) {
    return `${i18n.booking.participants.otherKidsInfosHeader.title} ${attendeeIndex + 1}`;
  }

  return `${i18n.booking.participants.otherAdultsInfosHeader.title} ${attendeeIndex}`;
};

export const cleanUselessAttendeesValues = ({
  adults,
  children,
}: AttendeesWithInfosType): AttendeesWithInfosType => ({
  children,
  // clear legalAgreement, it's not need in the store it's just to block the form
  adults: adults.map(adult => ({ ...adult, legalAgreement: undefined })),
});

export const isChild = (attendeeIdentifier: string) => attendeeIdentifier.includes('children');

export const getGmNumbersOptions = ({
  formik,
  i18n,
  previousGmNumberOptions,
}: {
  formik: Formik,
  i18n: CMSContent,
  previousGmNumberOptions: Array<{
    gmNumber: string,
    name: string,
    value: string,
    mainContactIndex?: number,
  }>,
}) => {
  // we use a negative gmNumber to know that it's a new gm
  let newGmNumber = -1;

  return [
    ...[...formik.values.adults, ...formik.values.children].reduce(
      (accGmNumbersOptions, attendee, index) => {
        if (!attendee.gmNumber && !attendee.mainContactId) {
          return accGmNumbersOptions;
        }

        const { firstName = '', lastName = '' } = attendee;
        const gmNumbersOptionsIndex = accGmNumbersOptions.findIndex(
          ({ gmNumber, value }) =>
            gmNumber === attendee.gmNumber || value === attendee.mainContactId,
        );

        if (gmNumbersOptionsIndex > -1) {
          accGmNumbersOptions[gmNumbersOptionsIndex].name += `, ${firstName} ${lastName}`;

          return accGmNumbersOptions;
        }

        if (!attendee.gmNumber && attendee.mainContactId < 0) {
          const previousNewHouseholdOption = previousGmNumberOptions.find(
            ({ gmNumber }) => gmNumber === attendee.mainContactId,
          );

          /**
           * if the main contact of a previously created new household is deleted (=> the user change his household)
           * and there is other attendees on this household, we prevent this household to be in the options
           * and we will reset them later in the provider
           */
          if (previousNewHouseholdOption && previousNewHouseholdOption.mainContactIndex !== index) {
            return accGmNumbersOptions;
          }

          // when a new household is created update the id for a potential next-one
          newGmNumber--;

          return [
            ...accGmNumbersOptions,
            {
              mainContactIndex: index,
              gmNumber: attendee.mainContactId,
              value: attendee.mainContactId,
              name: `${
                i18n.booking.participants.labels.gmNumber
              } ${lastName} - ${firstName} ${lastName}`,
            },
          ];
        }

        return [
          ...accGmNumbersOptions,
          {
            gmNumber: attendee.gmNumber,
            value: attendee.neolid,
            name: `${attendee.gmNumber} - ${firstName} ${lastName}`,
          },
        ];
      },
      [
        // select placeholder
        {
          name: i18n.booking.participants.labels.selectGmNumber,
          value: '',
          gmNumber: null,
        },
      ],
    ),
    {
      // select new household action option
      name: i18n.booking.participants.labels.createGmNumber,
      value: newGmNumber,
    },
  ];
};

const shouldHideField = ({
  connectedUserInfo,
  userIsSeller,
  formInitialValues,
  fieldName,
  attendeeIdentifier,
  // like for the phone value sometimes the name of a field is not the same between contact
  // form and the user store
  fieldNameConnectedUserOverride,
}: {
  connectedUserInfo?: userInfoType,
  userIsSeller: boolean,
  formInitialValues: GetInitialFormValuesType,
  fieldName: string,
  attendeeIdentifier: string,
  fieldNameConnectedUserOverride?: string,
}) => {
  if (
    connectedUserInfo &&
    ((fieldNameConnectedUserOverride && connectedUserInfo[fieldNameConnectedUserOverride]) ||
      connectedUserInfo[fieldName])
  ) {
    return true;
  }

  // if the attendee has a gmNumber he is identified and if he has a value for the field
  // we don't want to permit the seller to modified the field #11741
  return !!(
    userIsSeller &&
    get(formInitialValues, `${attendeeIdentifier}.neolid`) &&
    get(formInitialValues, `${attendeeIdentifier}.${fieldName}`)
  );
};

export const findDuplicateAttendees = (
  attendees: $ReadOnlyArray<{
    birthdate: number,
    firstName: string,
    lastName: string,
  }>,
) => {
  const duplicates = attendees.reduce((acc, { birthdate, lastName, firstName }, index) => {
    if (birthdate && lastName && firstName) {
      const key = `${birthdate}${lastName}${firstName}`.toLocaleLowerCase();
      if (acc[key]) {
        acc[key].push(index);
      } else {
        acc[key] = [index];
      }
    }
    return acc;
  }, {});

  Object.keys(duplicates).forEach(key => {
    if (duplicates[key].length < 2) {
      delete duplicates[key];
    }
  });

  // $FlowFixMe return indexes of duplicate attendees
  return flatten(Object.values(duplicates));
};

export {
  getCNILLegalMessage,
  getLegalInformation,
  submitOption,
  getLegalInputInformation,
  shouldDisplayLegalInformation,
  renderErrorUpdatingAttendees,
  getAllAttendeesIds,
  shouldHideField,
};
